import React, { useEffect, useMemo, useRef } from 'react'
import copy from 'copy-to-clipboard'

import {
  useDropdownState,
  useResponsiveDropdown,
  Dropdown,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { useInspectorContext } from '../../hooks/inspector'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'

import {
  SketchElement,
  isLayerElement,
  isFrameElement,
  SketchLayerCSSProperties,
} from '../../../../inspector'

import { useSubSketchElementsTreeAtRelativePosition } from './useSubSketchElementsTreeAtRelativePosition'
import { useTargetElement } from './useTargetElement'
import { ElementIcon } from './ElementIcon'
import { useGenerateLayerCSS } from '../FrameDetailInspector/useGenerateLayerCSS'
import { useExportModal } from '../FrameDetailInspector/sections/Exports/ExportModal'
import { useDownloadLayerAssets } from '../FrameDetailInspector/sections/Exports/useDownloadLayerAssets'
import { useShowExportErrorIfNeeded } from '../FrameDetailInspector/sections/Exports'
import {
  useCreateLayerLink,
  useShouldEnableCopyLinkToLayer,
} from '../../../../canvas/components/LayersTree/useCreateLayerLink'
import { useVersioning } from '../../../../versioning'
import { usePreview } from '../../../views/PreviewView/PreviewContext'

import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/img-markdown-link-share-17'
import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'

import * as S from './FrameDetailInspectorContextMenu.styles'
import { Coordinates } from '../../../../annotations/types'
import { VersionFragment } from '@sketch/gql-types'
import { useFlag } from '@sketch/modules-common'

import { FrameDetailInspectorContextMenu as FrameDetailInspectorContextMenuOld } from '../FrameDetailInspectorContextMenuOld'

type FrameDetailInspectorContextMenuProps = {
  className?: string
  /**
   * Menu position (relative to the artboard element)
   */
  contextMenuPosition: Coordinates | null
  onCloseMenu: () => void
}

export function FrameDetailInspectorContextMenu(
  props: FrameDetailInspectorContextMenuProps
) {
  const isInspectLayersTreeEnabled = useFlag('inspect-layers-tree')

  return isInspectLayersTreeEnabled ? (
    <FrameDetailInspectorContextMenuNext {...props} />
  ) : (
    <FrameDetailInspectorContextMenuOld {...props} />
  )
}

/**
 * The context menu opening when right clicking on the artboard canvas when
 * using the inspector.
 */
function FrameDetailInspectorContextMenuNext({
  className,
  contextMenuPosition,
  onCloseMenu,
}: FrameDetailInspectorContextMenuProps) {
  const {
    selectedElement,
    setSelectedElement,
    setHoveredElement,
    sketchSceneRootElement,
  } = useInspectorContext()

  const subSketchSceneRootElement =
    useSubSketchElementsTreeAtRelativePosition(contextMenuPosition)

  const dropdown = useDropdownState({
    onToggle: onCloseMenu,
  })
  const { setEnabled } = useCanvasInteractions()
  // Disable canvas interactions when context menu is open
  useEffect(() => {
    if (contextMenuPosition) {
      setEnabled(false)
      return () => {
        setEnabled(true)
      }
    }
  }, [contextMenuPosition, setEnabled])

  const elementsList = useMemo(() => {
    if (!subSketchSceneRootElement) {
      return []
    }

    /**
     * Only include the root element in the context menu if it's a frame.
     * For frames (artboards), we want to show the frame itself so users can select it.
     * For pages, we skip the root element and show only its children.
     *
     * We compare the UUIDs because the "subSketchSceneRootElement" contains a trimmed version of the
     * "sketchSceneRootElement"
     */
    if (
      sketchSceneRootElement?.elementUUID ===
      subSketchSceneRootElement?.elementUUID
    ) {
      // If the root element is a frame, include it in the context menu
      if (isFrameElement(subSketchSceneRootElement)) {
        return [subSketchSceneRootElement]
      }
      // Otherwise (for pages), show only the children
      return subSketchSceneRootElement.children
    }

    return [subSketchSceneRootElement]
  }, [subSketchSceneRootElement, sketchSceneRootElement])

  // Get the target element to apply the actions on.
  const targetElement = useTargetElement(elementsList, selectedElement)

  useEffect(() => {
    if (elementsList.length === 0 && contextMenuPosition) {
      // If the user right clicked to open menu but we realize that there is nothing
      // to show where the user clicked, reset the "open" position by closing the menu.
      onCloseMenu()
    }
  }, [contextMenuPosition, elementsList.length, onCloseMenu])

  // Auto select the target element when opening the menu
  // It could be that the target is already the selected element
  // in which case it doesn't change anything.
  useEffect(() => {
    if (targetElement) {
      setSelectedElement(targetElement)
    }
  }, [targetElement, setSelectedElement])

  if (
    !contextMenuPosition ||
    !subSketchSceneRootElement ||
    // We are already closing the menu in the use effect above but just in case,
    // let's avoid render the menu at all.
    elementsList.length === 0 ||
    !targetElement
  ) {
    return null
  }

  return (
    <S.DropdownWrapper
      {...dropdown}
      onClickOutside={dropdown.toggleDropdown}
      placement="auto-start"
      style={{
        left: contextMenuPosition.x,
        top: contextMenuPosition.y,
      }}
      contentStyle={{ maxWidth: 500 }}
      className={className}
      visible={true} // through the visible flag
      maxWidth="100%"
      usePortal={true}
    >
      <S.ContextMenuNav>
        <Dropdown.Header>{targetElement.name}</Dropdown.Header>
        <>
          <Dropdown.Divider />
          <SelectLayerSubmenu
            elementsList={elementsList}
            selectedLayerUuid={selectedElement?.elementUUID}
            onSelect={(sketchElement: SketchElement) => {
              setSelectedElement(sketchElement)
              onCloseMenu()
            }}
            onHoverMenuItem={setHoveredElement}
          />
        </>

        <CanvasContextMenuActions
          sketchElement={targetElement}
          onCloseMenu={onCloseMenu}
        />
      </S.ContextMenuNav>
    </S.DropdownWrapper>
  )
}

const SUBMENU_DROPDOWN_OFFSET: [number, number] = [0, 4]

type SelectLayerSubmenuProps = {
  elementsList: SketchElement[]
  selectedLayerUuid: string | undefined
  onSelect: (sketchElement: SketchElement) => void
  onHoverMenuItem: (sketchElement: SketchElement | null) => void
}

/**
 * A sub-menu that shows the list of layers under the cursor so
 * the user can select one of them more precisely.
 */
function SelectLayerSubmenu({
  elementsList,
  selectedLayerUuid,
  onSelect,
  onHoverMenuItem,
}: SelectLayerSubmenuProps) {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: () => (
      <S.ContextMenuNav>
        <SelectLayerList
          elementsList={elementsList}
          level={0}
          selectedLayerUuid={selectedLayerUuid}
          onSelect={onSelect}
          onHoverMenuItem={onHoverMenuItem}
        />
      </S.ContextMenuNav>
    ),
    placement: 'right-start',
    showOnHover: true,
    usePortal: true,
    offset: SUBMENU_DROPDOWN_OFFSET,
  })

  return (
    <>
      <S.SelectLayerItem {...buttonProps}>
        <S.SelectLayerItemContent>Select</S.SelectLayerItemContent>
        <S.ArrowIcon />
      </S.SelectLayerItem>
      {content}
    </>
  )
}

type SelectLayerListProps = {
  elementsList: SketchElement[]
  level: number
  selectedLayerUuid: string | undefined
  onSelect: (sketchElement: SketchElement) => void
  onHoverMenuItem: (sketchElement: SketchElement | null) => void
}
/**
 * A recursive component that renders a tree of elements into ordered lists
 */
function SelectLayerList({
  elementsList,
  level,
  selectedLayerUuid,
  onSelect,
  onHoverMenuItem,
}: SelectLayerListProps) {
  const ref = useRef<HTMLOListElement>(null)

  /*
    We are overriding the native "mousemove" event listener
    so it doesn't cause conflicts with the "onMouseEnter" and "onMouseLeave"
    events on the <li> below
   */
  useEffect(() => {
    const element = ref.current

    if (element) {
      const handleMouseMove = (event: MouseEvent) => {
        event.stopPropagation()
      }

      element.addEventListener('mousemove', handleMouseMove)

      return () => {
        element.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [])

  return (
    <ol key={`level${level}`} ref={ref}>
      {elementsList.map(sketchElement => {
        const isLayerMasked = isLayerElement(sketchElement)
          ? sketchElement.isLayerMasked
          : false

        return (
          <>
            <S.SelectLayerMenuItem
              key={sketchElement.identifier.toString()}
              $level={level}
              $isSelected={selectedLayerUuid === sketchElement.elementUUID}
              onClick={e => {
                // Make sure the click doesn't propagate to the parent
                // since this component is rendered recursively.
                e.stopPropagation()
                onSelect(sketchElement)
              }}
              onMouseEnter={() => {
                onHoverMenuItem(sketchElement)
              }}
              onMouseLeave={() => {
                onHoverMenuItem(null)
              }}
            >
              <ElementIcon
                elementType={sketchElement.type}
                isLayerMasked={isLayerMasked}
                isLayerExportable={sketchElement.isExportable}
              />
              <S.SelectListLayerName>
                {sketchElement.name}
              </S.SelectListLayerName>
            </S.SelectLayerMenuItem>
            <SelectLayerList
              elementsList={sketchElement.children}
              level={level + 1}
              selectedLayerUuid={selectedLayerUuid}
              onSelect={onSelect}
              onHoverMenuItem={onHoverMenuItem}
            />
          </>
        )
      })}
    </ol>
  )
}

type CanvasContextMenuActionsProps = {
  sketchElement: SketchElement
  onCloseMenu: () => void
}

type DownloadAllActionProps = {
  sketchElement: SketchElement
  currentVersion: VersionFragment
  onCloseMenu: () => void
}

function DownloadAllAction({
  sketchElement,
  currentVersion,
  onCloseMenu,
}: DownloadAllActionProps) {
  const { renderAssets, errorType } = useDownloadLayerAssets({
    layerOrArtboardId: sketchElement.exportId || '',
    currentVersion: currentVersion,
  })
  useShowExportErrorIfNeeded(errorType)

  const handleExportAllClick = () => {
    if (!sketchElement.exportId) return
    renderAssets()
    onCloseMenu()
  }

  if (!sketchElement.isExportable) return null

  return (
    <S.StyledDropdownItem onClick={handleExportAllClick}>
      <S.MenuIcon as={ExportIcon} />
      Export
    </S.StyledDropdownItem>
  )
}

function CanvasContextMenuActions({
  sketchElement,
  onCloseMenu,
}: CanvasContextMenuActionsProps) {
  const { showToast } = useToast()
  const { openExportModal } = useExportModal()
  const { currentVersion } = useVersioning()

  // Previews don't expose the `currentVersion` of a document, and so we don't have a document
  // identifier to pass to the export mutation. In this case we don't show the option to export
  // any predefined exports on the selected item.
  const previewContext = usePreview()
  const isPreview = Boolean(previewContext)

  const layerCSS = useGenerateLayerCSS(
    sketchElement as SketchLayerCSSProperties
  )
  const createLayerLink = useCreateLayerLink()

  const handleCopyCSSClick = () => {
    if (!layerCSS.css.length) return
    copy(layerCSS.toString())
    showToast('CSS copied')
    onCloseMenu()
  }

  const handleCopyLinkClick = () => {
    const url = createLayerLink(sketchElement.layerPathHash)
    if (url) {
      copy(url)
      showToast('Link copied')
    }
    onCloseMenu()
  }

  const handleExportAsClick = () => {
    openExportModal(sketchElement.identifier, sketchElement.name)
    onCloseMenu()
  }

  const shouldEnableCopyLink = useShouldEnableCopyLinkToLayer()

  return (
    <>
      {shouldEnableCopyLink && (
        <S.StyledDropdownItem onClick={handleCopyLinkClick}>
          <S.MenuIcon as={CopyLinkIcon} />
          Copy Link
        </S.StyledDropdownItem>
      )}
      {layerCSS.css.length > 0 && (
        <S.StyledDropdownItem onClick={handleCopyCSSClick}>
          <S.MenuIcon as={CopyCSSIcon} />
          Copy CSS
        </S.StyledDropdownItem>
      )}
      {!isPreview && currentVersion && (
        <DownloadAllAction
          sketchElement={sketchElement}
          currentVersion={currentVersion}
          onCloseMenu={onCloseMenu}
        />
      )}
      <S.StyledDropdownItem onClick={handleExportAsClick}>
        <S.MenuIcon as={ExportIcon} />
        Export as…
      </S.StyledDropdownItem>
    </>
  )
}
